// Copyright ©️ 2026 eVolve MEP, LLC

import { type WorkCell, type Task, useEvolvePatch } from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import type { WithNullable } from 'helpers/helper-types';
import { isNotNil, isNil } from 'helpers/isNotNil';
import {
  WorkCellSelect,
  type WorkCellSelectProps,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/MoveWorkCell/WorkCellSelect';

type Props = {
  opened: boolean;
  onClose: () => void;
  task: Pick<Task, 'taskId' | 'taskName'>;
  onTaskUpdated: (updatedTask: Task) => void;
} & Pick<WorkCellSelectProps, 'facility'>;

type ValidatedFormBody = {
  workCell: Pick<WorkCell, 'workCellName' | 'workCellId'>;
};
type FormBody = WithNullable<ValidatedFormBody>;

const WorkCellFields = ({ task, onTaskUpdated, facility }: Pick<Props, 'task' | 'onTaskUpdated' | 'facility'>) => {
  const { apiCall: updateTaskToWorkCell } = useEvolvePatch(`shop/task/moveToWorkCell`);
  const form = useForm<FormBody>({
    initialValues: {
      workCell: null,
    },
    validate: {
      workCell: (v) => isNil(v),
    },
  });

  const onSubmit = async (rawValues: typeof form.values) => {
    const values = rawValues as ValidatedFormBody;
    await updateTaskToWorkCell({
      taskIds: [task.taskId],
      workCellId: values.workCell.workCellId,
    }).then((updatedTasks) => {
      // This endpoint can be used to remap multiple tasks at once,
      // and while we are only updating one task at this moment, it's safer
      // to find our specific task to update (in case we ever update multiple in the future)
      const updatedTask = updatedTasks.find((t) => t.taskId === task.taskId);
      if (isNotNil(updatedTask)) {
        onTaskUpdated(updatedTask);
        notifications.show({
          title: 'Successfully updated',
          message: `${updatedTask.taskName} moved to ${updatedTask.workCellName}.`,
          color: 'green',
        });
      }
    });
  };

  useFormPopover(form, onSubmit);

  return (
    <Flex direction="column" gap={3}>
      <WorkCellSelect
        value={form.values.workCell}
        onChange={(v) => form.setFieldValue('workCell', v)}
        label="Select a work cell"
        taskIds={[task.taskId]}
        facility={facility}
      />
    </Flex>
  );
};

export const SetTaskWorkCellPopover = ({ opened, onClose, task, onTaskUpdated, facility }: Props) => (
  <FormPopover opened={opened} onClose={onClose}>
    <WorkCellFields task={task} onTaskUpdated={onTaskUpdated} facility={facility} />
  </FormPopover>
);
